import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";

export function ResetPasswordDialog({ open, onOpenChange, initialToken }) {
  const [step, setStep] = useState("request"); // 'request' | 'confirm'
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // When dialog opens with an initial token (from /reset/:token URL),
  // jump straight to the confirm step and pre-fill the field.
  useEffect(() => {
    if (open && initialToken) {
      setToken(initialToken);
      setStep("confirm");
    }
  }, [open, initialToken]);

  const reset = () => {
    setStep("request");
    setEmail("");
    setToken("");
    setNewPassword("");
    setBusy(false);
  };

  const close = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const requestToken = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setStep("confirm");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      toast.success("Password updated — sign in with your new password");
      close(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white rounded-sm border-zinc-200 sm:max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#0038FF]" />
            Reset password
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {step === "request"
              ? "Enter your email — we'll send you a reset link if the address is on file."
              : "Set a new password to finish resetting."}
          </DialogDescription>
        </DialogHeader>

        {step === "request" ? (
          <form onSubmit={requestToken} className="space-y-4" data-testid="reset-request-form">
            <div>
              <Label htmlFor="reset-email" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Email</Label>
              <Input
                id="reset-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="reset-email-input"
                className="mt-2 h-11 rounded-sm border-zinc-300 focus-visible:ring-[#0038FF]"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
                data-testid="reset-cancel-btn"
                className="h-11 rounded-sm border-zinc-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="reset-request-submit"
                className="h-11 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold"
              >
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={submitReset} className="space-y-4" data-testid="reset-confirm-form">
            <div>
              <Label htmlFor="reset-token" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Reset token</Label>
              <Input
                id="reset-token"
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                data-testid="reset-token-input"
                className="mt-2 h-11 rounded-sm border-zinc-300 font-mono text-sm focus-visible:ring-[#0038FF]"
              />
            </div>
            <div>
              <Label htmlFor="reset-new-password" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">New password</Label>
              <Input
                id="reset-new-password"
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-new-password-input"
                className="mt-2 h-11 rounded-sm border-zinc-300 focus-visible:ring-[#0038FF]"
              />
              <p className="mt-1 text-xs text-zinc-400">Minimum 6 characters.</p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep("request")}
                data-testid="reset-back-btn"
                className="h-11 rounded-sm border-zinc-300"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="reset-confirm-submit"
                className="h-11 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold"
              >
                {busy ? "Updating…" : "Update password"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
